import { reelSectionWords } from '@src/services/reel/reelSectionWords';
import { useMemo } from 'react';

import type { ClipPreviewEditorStore } from '@src/editor/ClipPreviewEditorStore';
import type { ReelSectionWord } from '@src/services/reel/reelSectionWords';

const NO_WORDS: ReelSectionWord[] = [];

/**
 * PLAT-11073 — the words of a single Section, for the scrub readout's
 * transcript strip.
 *
 * Given one Section's elements rather than the whole reel's: the strip only
 * ever shows the Section the playhead is in, and walking every Section's tokens
 * to render one of them would copy the transcript for nothing.
 *
 * Re-walks whenever the store's transcript signal changes, the same way
 * {@link useReelEditor} does — a word deleted in the Transcript tab is gone
 * from the strip too, provided the calling component is an `observer`.
 */
export function useSectionWords(
  editorStore: ClipPreviewEditorStore | null,
  elementIds: string[] | undefined,
): ReelSectionWord[] {
  // Read the observable *before* the memo so MobX records the dependency.
  const transcriptElements = editorStore?.transcriptElements;
  // The Section's identity for memo purposes: a reorder or an edit re-mints the
  // list, and nothing else about it can change.
  const elementKey = elementIds?.join(',') ?? '';

  return useMemo(() => {
    if (!editorStore || !elementIds?.length) return NO_WORDS;
    return reelSectionWords(editorStore.editor.timeline, elementIds);
    // `elementKey` stands in for `elementIds`, and `transcriptElements` is the
    // change signal rather than an input — the words are read off the timeline.
  }, [editorStore, elementKey, transcriptElements]);
}
